import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import type { SelectChangeEvent } from "@mui/material/Select";
import { useAdminEventReassignmentApi } from "../hooks/useAdminEventReassignmentApi";
import type { ReassignmentMode, ReassignmentPreviewResponse, ReassignmentPreviewRow } from "../shared/adminEventReassignment";
import type { NumberRangeDescription } from "../shared/NumberRange";

type Props = {
  active: boolean;
};

type RangeOption = {
  id: number;
  digit: number;
  label: string;
};

/** Prefer English display name, then any language — dropdown labels are human-readable names only. */
const displayNameForOption = (descriptions?: NumberRangeDescription[]): string | null => {
  if (!Array.isArray(descriptions)) return null;
  const eng = descriptions.find((d) => (d.language_code || "").toLowerCase().startsWith("eng"));
  const fromEng = (eng?.display_name || "").trim();
  if (fromEng) return fromEng;
  for (const d of descriptions) {
    const n = (d.display_name || "").trim();
    if (n) return n;
  }
  return null;
};

const toRangeOption = (item: {
  id: number;
  digit: number;
  descriptions?: NumberRangeDescription[];
}): RangeOption => {
  const name = displayNameForOption(item.descriptions);
  return {
    id: item.id,
    digit: item.digit,
    label: name ?? `Digit ${item.digit}`,
  };
};

/** Sub-functions without a display name are omitted from source/target sub dropdowns. */
const subOptionsWithDisplayName = (
  rows: Array<{ id: number; digit: number; descriptions?: NumberRangeDescription[] }>
): RangeOption[] => {
  const out: RangeOption[] = [];
  for (const row of rows) {
    const name = displayNameForOption(row.descriptions);
    if (name) out.push({ id: row.id, digit: row.digit, label: name });
  }
  return out;
};

export const canApplyReassignment = (
  loading: boolean,
  conflictCount: number,
  readyCount: number,
  confirmText: string
): boolean => {
  return (
    !loading &&
    conflictCount <= 0 &&
    readyCount > 0 &&
    confirmText.trim().toUpperCase() === "APPLY"
  );
};

const pairRoleChipLabel = (role: ReassignmentPreviewRow["pair_role"]): string | null => {
  if (role === "anchor") return "Requested ID";
  if (role === "counterpart") return "Auto-included pair";
  return null;
};

export default function AdminEventReassignmentPanel({ active }: Props) {
  const { fetchBaseFunctions, fetchSubFunctions, previewReassignment, applyReassignment } =
    useAdminEventReassignmentApi();
  const [mode, setMode] = React.useState<ReassignmentMode>("single");
  const [globalEventId, setGlobalEventId] = React.useState("");
  const [sourceBaseId, setSourceBaseId] = React.useState("");
  const [sourceSubId, setSourceSubId] = React.useState("");
  const [targetBaseId, setTargetBaseId] = React.useState("");
  const [targetSubId, setTargetSubId] = React.useState("");
  const [bases, setBases] = React.useState<RangeOption[]>([]);
  const [sourceSubs, setSourceSubs] = React.useState<RangeOption[]>([]);
  const [targetSubs, setTargetSubs] = React.useState<RangeOption[]>([]);
  const [preview, setPreview] = React.useState<ReassignmentPreviewResponse | null>(null);
  const [confirmText, setConfirmText] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!active) return;
    void (async () => {
      const rows = await fetchBaseFunctions();
      setBases(rows.map(toRangeOption));
    })();
  }, [active, fetchBaseFunctions]);

  React.useEffect(() => {
    if (!sourceBaseId || mode !== "bulk") return;
    void (async () => {
      const rows = await fetchSubFunctions(Number(sourceBaseId));
      const filtered = subOptionsWithDisplayName(rows);
      setSourceSubs(filtered);
      setSourceSubId((prev) => (prev && filtered.some((s) => String(s.id) === prev) ? prev : ""));
    })();
  }, [sourceBaseId, mode, fetchSubFunctions]);

  React.useEffect(() => {
    if (!targetBaseId) return;
    void (async () => {
      const rows = await fetchSubFunctions(Number(targetBaseId));
      const filtered = subOptionsWithDisplayName(rows);
      setTargetSubs(filtered);
      setTargetSubId((prev) => (prev && filtered.some((s) => String(s.id) === prev) ? prev : ""));
    })();
  }, [targetBaseId, fetchSubFunctions]);

  const runPreview = async () => {
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const payload =
        mode === "single"
          ? {
              mode,
              global_event_id: Number(globalEventId),
              target_base_id: Number(targetBaseId),
              target_sub_id: Number(targetSubId),
            }
          : {
              mode,
              source_base_id: Number(sourceBaseId),
              source_sub_id: Number(sourceSubId),
              target_base_id: Number(targetBaseId),
              target_sub_id: Number(targetSubId),
            };
      const res = await previewReassignment(payload as any);
      setPreview(res);
    } catch (e: any) {
      setError(e?.responseBody || e?.message || "Preview failed.");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  const apply = async () => {
    if (!preview) return;
    setError(null);
    setSuccess(null);
    setLoading(true);
    try {
      const payload =
        mode === "single"
          ? {
              mode,
              global_event_id: Number(globalEventId),
              target_base_id: Number(targetBaseId),
              target_sub_id: Number(targetSubId),
            }
          : {
              mode,
              source_base_id: Number(sourceBaseId),
              source_sub_id: Number(sourceSubId),
              target_base_id: Number(targetBaseId),
              target_sub_id: Number(targetSubId),
            };
      const res = await applyReassignment({
        ...(payload as any),
        preview_token: preview.preview_token,
        confirm_text: confirmText,
      });
      setSuccess(`Applied ${res.applied_count} event reassignment(s).`);
      setConfirmText("");
      await runPreview();
    } catch (e: any) {
      setError(e?.responseBody || e?.message || "Apply failed.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        Advanced operation. Always run preview first and verify every mapping before apply.
        In <strong>Single event</strong> mode, the preview also includes the active rising/resolving
        counterpart (latest non-superseded head at event code ±10 000) when one exists (bulk mode already
        moves both bands).
      </Alert>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ mb: 1.5 }} flexWrap="wrap">
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="reassignment-mode-label">Mode</InputLabel>
          <Select
            labelId="reassignment-mode-label"
            label="Mode"
            value={mode}
            onChange={(e: SelectChangeEvent) => setMode(e.target.value as ReassignmentMode)}
          >
            <MenuItem value="single">Single event to target base/sub</MenuItem>
            <MenuItem value="bulk">Bulk: source sub to target base/sub</MenuItem>
          </Select>
        </FormControl>

        {mode === "single" ? (
          <TextField
            size="small"
            label="ID"
            value={globalEventId}
            onChange={(e) => setGlobalEventId(e.target.value)}
            sx={{ minWidth: 170 }}
          />
        ) : (
          <>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="source-base-label">Source base</InputLabel>
              <Select
                labelId="source-base-label"
                label="Source base"
                value={sourceBaseId}
                onChange={(e) => {
                  setSourceBaseId(e.target.value);
                  setSourceSubId("");
                }}
              >
                {bases.map((b) => (
                  <MenuItem key={b.id} value={String(b.id)}>
                    {b.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel id="source-sub-label">Source sub</InputLabel>
              <Select
                labelId="source-sub-label"
                label="Source sub"
                value={sourceSubId}
                onChange={(e) => setSourceSubId(e.target.value)}
              >
                {sourceSubs.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="target-base-label">Target base</InputLabel>
          <Select
            labelId="target-base-label"
            label="Target base"
            value={targetBaseId}
            onChange={(e) => {
              setTargetBaseId(e.target.value);
              setTargetSubId("");
            }}
          >
            {bases.map((b) => (
              <MenuItem key={b.id} value={String(b.id)}>
                {b.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="target-sub-label">Target sub</InputLabel>
          <Select
            labelId="target-sub-label"
            label="Target sub"
            value={targetSubId}
            onChange={(e) => setTargetSubId(e.target.value)}
          >
            {targetSubs.map((s) => (
              <MenuItem key={s.id} value={String(s.id)}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="contained" onClick={() => void runPreview()} disabled={loading}>
          Preview
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1.5 }}>{success}</Alert>}

      {preview && (
        <Box>
          {preview.mode === "single" && preview.pair_counterpart_included && (
            <Alert severity="info" sx={{ mb: 1.5 }}>
              A <strong>{preview.pair_counterpart_side}</strong> counterpart (ID{" "}
              <strong>{preview.pair_counterpart_global_event_id}</strong>) was found and will be
              reassigned together with the requested event (ID{" "}
              <strong>{preview.pair_anchor_global_event_id}</strong>). Review both rows before apply.
            </Alert>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap">
            <Chip size="small" label={`Total ${preview.total}`} />
            <Chip size="small" color="success" label={`Ready ${preview.ready_count}`} />
            <Chip size="small" color="warning" label={`Conflicts ${preview.conflict_count}`} />
            <Chip size="small" label={`Unchanged ${preview.unchanged_count}`} />
            <Chip size="small" label={`Skipped ${preview.skipped_count}`} />
          </Stack>
          <Table size="small" sx={{ mb: 1.5 }}>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Side</TableCell>
                <TableCell>Pair</TableCell>
                <TableCell>Version</TableCell>
                <TableCell>Old Event Code</TableCell>
                <TableCell>New Event Code</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Reason</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.rows.map((row) => {
                const pairLabel = pairRoleChipLabel(row.pair_role);
                return (
                <TableRow
                  key={row.global_event_id}
                  sx={
                    row.pair_role === "counterpart"
                      ? { bgcolor: (t) => t.palette.action.hover }
                      : undefined
                  }
                >
                  <TableCell>{row.global_event_id}</TableCell>
                  <TableCell>
                    {row.pair_side ? (
                      <Chip
                        size="small"
                        label={row.pair_side === "resolving" ? "Resolving" : "Rising"}
                        color={row.pair_side === "resolving" ? "secondary" : "primary"}
                        variant="outlined"
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {pairLabel ? (
                      <Chip
                        size="small"
                        label={pairLabel}
                        color={row.pair_role === "counterpart" ? "info" : "default"}
                        variant={row.pair_role === "counterpart" ? "filled" : "outlined"}
                      />
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {row.version ?? "—"}
                    {row.version_new != null && row.version_new !== row.version
                      ? ` → ${row.version_new}`
                      : ""}
                  </TableCell>
                  <TableCell>{row.event_code_old}</TableCell>
                  <TableCell>{row.event_code_new}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>{row.reason || "—"}</TableCell>
                </TableRow>
              );
              })}
            </TableBody>
          </Table>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
            <TextField
              size="small"
              label="Type APPLY to confirm"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
            />
            <Button
              variant="contained"
              color="error"
              disabled={!canApplyReassignment(
                loading,
                preview.conflict_count,
                preview.ready_count,
                confirmText
              )}
              onClick={() => void apply()}
            >
              Apply reassignment
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
